"use client";

import { c as _c } from "react/compiler-runtime";
import { CalendarOutlined, MailOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, DatePicker, Divider, Dropdown, Form, Input, InputNumber, Modal, Radio, Space } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/zh-cn";
import "dayjs/locale/zh-tw";
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import zhTW from "antd/locale/zh_TW";
import { useLocale, useTranslations } from "next-intl";
import React, { useCallback, useState } from "react";
import styles from "./page.module.sass";
const GOOGLE_CALENDAR_EMBED_URL = "https://calendar.google.com/calendar/embed?height=1280&wkst=1&ctz=Asia%2FTaipei&showPrint=0&src=Y2hhbmd3ZWkxMDA2QGdtYWlsLmNvbQ&src=MzdiY2Q1MjRhMWMwZjcwMDQ4YzVlNDUxZDEwNDRlZmQ1YTc3ZTA0YmM4NWE3YTg2MDg0ZGM3MWY1MGMzMDlmN0Bncm91cC5jYWxlbmRhci5nb29nbGUuY29t&src=MGI2NzlmNHZyZmpiY3E4OTJlODNraGVxZjhAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=ZThzOW90cWtjcmVsOWo5dHFtNHAwMDc1bHE3c3JoNWdAaW1wb3J0LmNhbGVuZGFyLmdvb2dsZS5jb20&src=ZW4udGFpd2FuI2hvbGlkYXlAZ3JvdXAudi5jYWxlbmRhci5nb29nbGUuY29t&src=bGlicmVmNjE5NkBnbWFpbC5jb20&src=ZjYxMjFlMDU2NTg3ODM4NzI5YWQ4NDBhNmM0YzcxYTE3Yzg5MjAwYTc1MWJlOGNjNjQ5MGJhNzVmMTFjNDc1YUBncm91cC5jYWxlbmRhci5nb29nbGUuY29t&src=emgtY24uY2hpbmEjaG9saWRheUBncm91cC52LmNhbGVuZGFyLmdvb2dsZS5jb20&src=emgtdHcudGFpd2FuI2hvbGlkYXlAZ3JvdXAudi5jYWxlbmRhci5nb29nbGUuY29t&src=b3U2N2tucWFqNXNxODMzdXRpN2JhNmJraHNAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=ZmE1ODlpdGg0Zm85MDFvOWxjdDQ2NGZncDRAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=Y185ZWYyZGRiMzY0ZTU4ZWE1MWUyZDcxNjhkNjYxNWExNTE1Yzg2NzBkOTM0NTE5NWI5NjliODZkNzJhNGI3NGY4QGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20&src=MnI1ZWZuNDZuMzRoNm9tN3NtMjVtaWQ4ZThAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=YjRvc211NTdqZzlwOXU1dTZ2NWF1Zm0wbDhAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=ODcxbTc1MHN1b212NTY3NXNqZnA4cHU3cWtAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=ZmdzNzcwNGp0YTIwcGZqY21rb2txY2J1cmtAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=aDlnbHR0ZDVjM2YwZW9xMGd0Mmhrc3Y2NGdAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=amlqZHJvcjJhbmRrdmtpNWU4YmoyanUzdGNAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=emgtY24uaG9uZ19rb25nI2hvbGlkYXlAZ3JvdXAudi5jYWxlbmRhci5nb29nbGUuY29t&color=%23039be5&color=%239e69af&color=%237986cb&color=%23f6bf26&color=%230b8043&color=%23a79b8e&color=%23c0ca33&color=%230b8043&color=%234285f4&color=%233f51b5&color=%23009688&color=%23d81b60&color=%23f6bf26&color=%23d50000&color=%237cb342&color=%23a79b8e&color=%238e24aa&color=%23e67c73&color=%23f4511e";
const BOOK_URL = "https://calendar.app.google/ks4sRpiehtRuQW3q6";
type AppLocale = "en-US" | "zh-Hans" | "zh-Hant";
type RequestLanguage = "zh-Hant" | "zh-Hans" | "en";
type Recipient = "gmail" | "qq";
const RECIPIENT_EMAILS: Record<Recipient, string> = {
  gmail: "changwei1006@gmail.com",
  qq: "changwei1006@qq.com"
};
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";
const TIME_FORMAT = "HH:mm";

// Keep in sync with the `duration` InputNumber min/max and step below.
const DURATION_MIN_MINUTES = 15;
const DURATION_MAX_MINUTES = 480;
const DURATION_STEP_MINUTES = 15;

/** Snaps a duration onto the allowed {min, step, max} grid. */
function clampDuration(minutes: number): number {
  if (!Number.isFinite(minutes)) return DURATION_MIN_MINUTES;
  const clamped = Math.min(DURATION_MAX_MINUTES, Math.max(DURATION_MIN_MINUTES, Math.round(minutes)));
  const steps = Math.round((clamped - DURATION_MIN_MINUTES) / DURATION_STEP_MINUTES);
  const snapped = DURATION_MIN_MINUTES + steps * DURATION_STEP_MINUTES;
  return Math.min(DURATION_MAX_MINUTES, Math.max(DURATION_MIN_MINUTES, snapped));
}
const dayjsLocaleByAppLocale: Record<AppLocale, string> = {
  "en-US": "en",
  "zh-Hans": "zh-cn",
  "zh-Hant": "zh-tw"
};
const antdLocaleByAppLocale: Record<AppLocale, typeof enUS> = {
  "en-US": enUS,
  "zh-Hans": zhCN,
  "zh-Hant": zhTW
};
export default function CalendarPage(_props) {
  const $ = _c(207);
  const t = useTranslations("calendar");
  const tr = useTranslations("calendarRequest");
  const locale = useLocale();
  const appLocale = locale === "zh-Hans" || locale === "zh-Hant" ? locale : "en-US";
  const [requestOpen, setRequestOpen] = useState(false);
  const [language, setLanguage] = useState("zh-Hant");
  const [form] = Form.useForm();
  const djLocale = dayjsLocaleByAppLocale[appLocale];
  const openBook = _temp;
  let t0;
  if ($[0] !== tr) {
    t0 = tr("languageZHT");
    $[0] = tr;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      key: "zh-Hant" as const,
      value: "zh-Hant" as const,
      label: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== tr) {
    t2 = tr("languageZHS");
    $[4] = tr;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = {
      key: "zh-Hans" as const,
      value: "zh-Hans" as const,
      label: t2
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== tr) {
    t4 = tr("languageEN");
    $[8] = tr;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = {
      key: "en" as const,
      value: "en" as const,
      label: t4
    };
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t3 || $[14] !== t5) {
    t6 = [t1, t3, t5];
    $[12] = t1;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const languageItems = t6;
  let t7;
  if ($[16] !== djLocale || $[17] !== form) {
    t7 = (startTime, duration) => {
      if (startTime && duration) {
        form.setFieldValue("endTime", startTime.add(clampDuration(duration), "minute").locale(djLocale));
      }
    };
    $[16] = djLocale;
    $[17] = form;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const syncEndTime = t7;
  let t8;
  if ($[19] !== form || $[20] !== syncEndTime) {
    t8 = value => {
      syncEndTime(value, form.getFieldValue("duration"));
    };
    $[19] = form;
    $[20] = syncEndTime;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleStartTimeChange = t8;
  let t9;
  if ($[22] !== form || $[23] !== syncEndTime) {
    t9 = value_0 => {
      syncEndTime(form.getFieldValue("startTime"), value_0);
    };
    $[22] = form;
    $[23] = syncEndTime;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleDurationChange = t9;
  let t10;
  if ($[25] !== form) {
    t10 = value_1 => {
      const startTime_0 = form.getFieldValue("startTime") as Dayjs | undefined;
      if (startTime_0 && value_1) {
        const diffMinutes = Math.round(value_1.diff(startTime_0, "minute", true));
        form.setFieldValue("duration", clampDuration(diffMinutes));
      }
    };
    $[25] = form;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleEndTimeChange = t10;
  let t11;
  if ($[27] !== form || $[28] !== tr) {
    t11 = (_rule, value_2) => {
      const startTime_1 = form.getFieldValue("startTime") as Dayjs | undefined;
      const endTime = value_2 as Dayjs | undefined;
      if (startTime_1 && endTime && endTime.isBefore(startTime_1)) {
        return Promise.reject(new Error(tr("endTimeError")));
      }
      return Promise.resolve();
    };
    $[27] = form;
    $[28] = tr;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const endTimeValidator = t11;
  let t12;
  if ($[30] !== djLocale || $[31] !== form || $[32] !== tr) {
    t12 = async () => {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return;
      }
      const startText = values.startTime.locale(djLocale).format(DATE_TIME_FORMAT);
      const endText = values.endTime.locale(djLocale).format(DATE_TIME_FORMAT);
      const durationText = `${values.duration} ${tr("messageMinute")}`;
      const body = [`${tr("messageGreeting")}\n${tr("messageIntro")}`, `- ${tr("messageStartTime")}: ${startText}`, `- ${tr("messageEndTime")}: ${endText}`, `- ${tr("messageDuration")}: ${durationText}`, `- ${tr("messageSubject")}: ${values.subject}`, `- ${tr("messageLocation")}: ${values.location}`, "", tr("messageClosing"), tr("messageSignOff")].join("\n");
      const mailto = `mailto:${RECIPIENT_EMAILS[values.recipient]}?subject=${encodeURIComponent(tr("mailTitle"))}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      setRequestOpen(false);
    };
    $[30] = djLocale;
    $[31] = form;
    $[32] = tr;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const handleSubmit = t12;
  let t13;
  if ($[34] !== t) {
    t13 = t("pageTitle");
    $[34] = t;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t13) {
    t14 = <h1 className={styles.title}>{t13}</h1>;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Divider data-sentry-element="Divider" data-sentry-source-file="page.tsx" />;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <CalendarOutlined />;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t) {
    t17 = t("book");
    $[40] = t;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t17) {
    t18 = <Button className={styles.book_button} type="primary" icon={t16} onClick={openBook} data-sentry-element="Button" data-sentry-source-file="page.tsx">{t17}</Button>;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = t20 => {
      const {
        key
      } = t20;
      setLanguage(key as RequestLanguage);
      setRequestOpen(true);
    };
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== languageItems) {
    t20 = {
      items: languageItems,
      onClick: t19
    };
    $[45] = languageItems;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = ["click"];
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <MailOutlined />;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== tr) {
    t23 = tr("title");
    $[49] = tr;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t23) {
    t24 = <Button className={styles.request_button} icon={t22} data-sentry-element="Button" data-sentry-source-file="page.tsx">{t23}</Button>;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t20 || $[54] !== t24) {
    t25 = <Dropdown menu={t20} trigger={t21} data-sentry-element="Dropdown" data-sentry-source-file="page.tsx">{t24}</Dropdown>;
    $[53] = t20;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t18 || $[57] !== t25) {
    t26 = <Space className={styles.toolbar} data-sentry-element="Space" data-sentry-source-file="page.tsx">{t18}{t25}</Space>;
    $[56] = t18;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t) {
    t27 = t("pageTitle");
    $[59] = t;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      width: "100%",
      height: "1280px"
    };
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t27) {
    t29 = <div className={styles.iframe_wrap}><iframe src={GOOGLE_CALENDAR_EMBED_URL} title={t27} style={t28} loading="lazy" /></div>;
    $[62] = t27;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t14 || $[65] !== t26 || $[66] !== t29) {
    t30 = <main>{t14}{t15}{t26}{t29}</main>;
    $[64] = t14;
    $[65] = t26;
    $[66] = t29;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== tr) {
    t31 = tr("title");
    $[68] = tr;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setRequestOpen(false);
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== tr) {
    t33 = tr("submitLabel");
    $[71] = tr;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== tr) {
    t34 = tr("cancelLabel");
    $[73] = tr;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  const t35 = antdLocaleByAppLocale[appLocale];
  let t36;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      recipient: "gmail" as Recipient
    };
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] !== tr) {
    t37 = tr("languageLabel");
    $[76] = tr;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== tr) {
    t38 = tr("languageLabel");
    $[78] = tr;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = e => setLanguage(e.target.value as RequestLanguage);
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== language || $[82] !== languageItems || $[83] !== t38) {
    t40 = <Radio.Group aria-label={t38} value={language} onChange={t39} options={languageItems} data-sentry-element="Radio.Group" data-sentry-source-file="page.tsx" />;
    $[81] = language;
    $[82] = languageItems;
    $[83] = t38;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== t37 || $[86] !== t40) {
    t41 = <Form.Item label={t37} data-sentry-element="Form.Item" data-sentry-source-file="page.tsx">{t40}</Form.Item>;
    $[85] = t37;
    $[86] = t40;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] !== tr) {
    t42 = tr("recipientLabel");
    $[88] = tr;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  let t43;
  if ($[90] !== tr) {
    t43 = tr("recipientLabel");
    $[90] = tr;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] !== tr) {
    t44 = tr("recipientGmail");
    $[92] = tr;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] !== t44) {
    t45 = {
      label: t44,
      value: "gmail"
    };
    $[94] = t44;
    $[95] = t45;
  } else {
    t45 = $[95];
  }
  let t46;
  if ($[96] !== tr) {
    t46 = tr("recipientQQ");
    $[96] = tr;
    $[97] = t46;
  } else {
    t46 = $[97];
  }
  let t47;
  if ($[98] !== t46) {
    t47 = {
      label: t46,
      value: "qq"
    };
    $[98] = t46;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  let t48;
  if ($[100] !== t45 || $[101] !== t47) {
    t48 = [t45, t47];
    $[100] = t45;
    $[101] = t47;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] !== t43 || $[104] !== t48) {
    t49 = <Radio.Group aria-label={t43} options={t48} data-sentry-element="Radio.Group" data-sentry-source-file="page.tsx" />;
    $[103] = t43;
    $[104] = t48;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  let t50;
  if ($[106] !== t42 || $[107] !== t49) {
    t50 = <Form.Item name="recipient" label={t42} data-sentry-element="Form.Item" data-sentry-source-file="page.tsx">{t49}</Form.Item>;
    $[106] = t42;
    $[107] = t49;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== tr) {
    t51 = tr("startTimeLabel");
    $[109] = tr;
    $[110] = t51;
  } else {
    t51 = $[110];
  }
  let t52;
  if ($[111] !== tr) {
    t52 = tr("startTimeRequired");
    $[111] = tr;
    $[112] = t52;
  } else {
    t52 = $[112];
  }
  let t53;
  if ($[113] !== t52) {
    t53 = [{
      required: true,
      message: t52
    }];
    $[113] = t52;
    $[114] = t53;
  } else {
    t53 = $[114];
  }
  let t54;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      format: TIME_FORMAT
    };
    $[115] = t54;
  } else {
    t54 = $[115];
  }
  let t55;
  if ($[116] !== handleStartTimeChange) {
    t55 = <DatePicker showTime={t54} format={DATE_TIME_FORMAT} needConfirm={false} onChange={handleStartTimeChange} data-sentry-element="DatePicker" data-sentry-source-file="page.tsx" />;
    $[116] = handleStartTimeChange;
    $[117] = t55;
  } else {
    t55 = $[117];
  }
  let t56;
  if ($[118] !== t51 || $[119] !== t53 || $[120] !== t55) {
    t56 = <Form.Item name="startTime" label={t51} rules={t53} data-sentry-element="Form.Item" data-sentry-source-file="page.tsx">{t55}</Form.Item>;
    $[118] = t51;
    $[119] = t53;
    $[120] = t55;
    $[121] = t56;
  } else {
    t56 = $[121];
  }
  let t57;
  if ($[122] !== tr) {
    t57 = tr("durationLabel");
    $[122] = tr;
    $[123] = t57;
  } else {
    t57 = $[123];
  }
  let t58;
  if ($[124] !== tr) {
    t58 = tr("durationRequired");
    $[124] = tr;
    $[125] = t58;
  } else {
    t58 = $[125];
  }
  let t59;
  if ($[126] !== t58) {
    t59 = {
      required: true,
      message: t58
    };
    $[126] = t58;
    $[127] = t59;
  } else {
    t59 = $[127];
  }
  let t60;
  if ($[128] !== tr) {
    t60 = tr("durationRangeError");
    $[128] = tr;
    $[129] = t60;
  } else {
    t60 = $[129];
  }
  let t61;
  if ($[130] !== t60) {
    t61 = {
      type: "number",
      min: DURATION_MIN_MINUTES,
      max: DURATION_MAX_MINUTES,
      message: t60
    };
    $[130] = t60;
    $[131] = t61;
  } else {
    t61 = $[131];
  }
  let t62;
  if ($[132] !== t59 || $[133] !== t61) {
    t62 = [t59, t61];
    $[132] = t59;
    $[133] = t61;
    $[134] = t62;
  } else {
    t62 = $[134];
  }
  let t63;
  if ($[135] !== handleDurationChange) {
    t63 = <InputNumber min={DURATION_MIN_MINUTES} max={DURATION_MAX_MINUTES} step={DURATION_STEP_MINUTES} onChange={handleDurationChange} data-sentry-element="InputNumber" data-sentry-source-file="page.tsx" />;
    $[135] = handleDurationChange;
    $[136] = t63;
  } else {
    t63 = $[136];
  }
  let t64;
  if ($[137] !== t57 || $[138] !== t62 || $[139] !== t63) {
    t64 = <Form.Item name="duration" label={t57} initialValue={DURATION_MIN_MINUTES * 2} rules={t62} data-sentry-element="Form.Item" data-sentry-source-file="page.tsx">{t63}</Form.Item>;
    $[137] = t57;
    $[138] = t62;
    $[139] = t63;
    $[140] = t64;
  } else {
    t64 = $[140];
  }
  let t65;
  if ($[141] !== tr) {
    t65 = tr("endTimeLabel");
    $[141] = tr;
    $[142] = t65;
  } else {
    t65 = $[142];
  }
  let t66;
  if ($[143] !== tr) {
    t66 = tr("endTimeHelp");
    $[143] = tr;
    $[144] = t66;
  } else {
    t66 = $[144];
  }
  let t67;
  if ($[145] !== tr) {
    t67 = tr("endTimeRequired");
    $[145] = tr;
    $[146] = t67;
  } else {
    t67 = $[146];
  }
  let t68;
  if ($[147] !== t67) {
    t68 = {
      required: true,
      message: t67
    };
    $[147] = t67;
    $[148] = t68;
  } else {
    t68 = $[148];
  }
  let t69;
  if ($[149] !== endTimeValidator) {
    t69 = {
      validator: endTimeValidator
    };
    $[149] = endTimeValidator;
    $[150] = t69;
  } else {
    t69 = $[150];
  }
  let t70;
  if ($[151] !== t68 || $[152] !== t69) {
    t70 = [t68, t69];
    $[151] = t68;
    $[152] = t69;
    $[153] = t70;
  } else {
    t70 = $[153];
  }
  let t71;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = {
      format: TIME_FORMAT
    };
    $[154] = t71;
  } else {
    t71 = $[154];
  }
  let t72;
  if ($[155] !== handleEndTimeChange) {
    t72 = <DatePicker showTime={t71} format={DATE_TIME_FORMAT} needConfirm={false} onChange={handleEndTimeChange} data-sentry-element="DatePicker" data-sentry-source-file="page.tsx" />;
    $[155] = handleEndTimeChange;
    $[156] = t72;
  } else {
    t72 = $[156];
  }
  let t73;
  if ($[157] !== t65 || $[158] !== t66 || $[159] !== t70 || $[160] !== t72) {
    t73 = <Form.Item name="endTime" label={t65} extra={t66} rules={t70} data-sentry-element="Form.Item" data-sentry-source-file="page.tsx">{t72}</Form.Item>;
    $[157] = t65;
    $[158] = t66;
    $[159] = t70;
    $[160] = t72;
    $[161] = t73;
  } else {
    t73 = $[161];
  }
  let t74;
  if ($[162] !== tr) {
    t74 = tr("subjectLabel");
    $[162] = tr;
    $[163] = t74;
  } else {
    t74 = $[163];
  }
  let t75;
  if ($[164] !== tr) {
    t75 = tr("subjectRequired");
    $[164] = tr;
    $[165] = t75;
  } else {
    t75 = $[165];
  }
  let t76;
  if ($[166] !== t75) {
    t76 = [{
      required: true,
      message: t75
    }];
    $[166] = t75;
    $[167] = t76;
  } else {
    t76 = $[167];
  }
  let t77;
  if ($[168] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <Input.TextArea rows={3} data-sentry-element="Input.TextArea" data-sentry-source-file="page.tsx" />;
    $[168] = t77;
  } else {
    t77 = $[168];
  }
  let t78;
  if ($[169] !== t74 || $[170] !== t76) {
    t78 = <Form.Item name="subject" label={t74} rules={t76} data-sentry-element="Form.Item" data-sentry-source-file="page.tsx">{t77}</Form.Item>;
    $[169] = t74;
    $[170] = t76;
    $[171] = t78;
  } else {
    t78 = $[171];
  }
  let t79;
  if ($[172] !== tr) {
    t79 = tr("locationLabel");
    $[172] = tr;
    $[173] = t79;
  } else {
    t79 = $[173];
  }
  let t80;
  if ($[174] !== tr) {
    t80 = tr("locationHelp");
    $[174] = tr;
    $[175] = t80;
  } else {
    t80 = $[175];
  }
  let t81;
  if ($[176] !== tr) {
    t81 = tr("locationRequired");
    $[176] = tr;
    $[177] = t81;
  } else {
    t81 = $[177];
  }
  let t82;
  if ($[178] !== t81) {
    t82 = [{
      required: true,
      message: t81
    }];
    $[178] = t81;
    $[179] = t82;
  } else {
    t82 = $[179];
  }
  let t83;
  if ($[180] === Symbol.for("react.memo_cache_sentinel")) {
    t83 = <Input.TextArea rows={2} data-sentry-element="Input.TextArea" data-sentry-source-file="page.tsx" />;
    $[180] = t83;
  } else {
    t83 = $[180];
  }
  let t84;
  if ($[181] !== t79 || $[182] !== t80 || $[183] !== t82) {
    t84 = <Form.Item name="location" label={t79} extra={t80} rules={t82} data-sentry-element="Form.Item" data-sentry-source-file="page.tsx">{t83}</Form.Item>;
    $[181] = t79;
    $[182] = t80;
    $[183] = t82;
    $[184] = t84;
  } else {
    t84 = $[184];
  }
  let t85;
  if ($[185] !== form || $[186] !== t41 || $[187] !== t50 || $[188] !== t56 || $[189] !== t64 || $[190] !== t73 || $[191] !== t78 || $[192] !== t84) {
    t85 = <Form form={form} layout="vertical" preserve={false} initialValues={t36} data-sentry-element="Form" data-sentry-source-file="page.tsx">{t41}{t50}{t56}{t64}{t73}{t78}{t84}</Form>;
    $[185] = form;
    $[186] = t41;
    $[187] = t50;
    $[188] = t56;
    $[189] = t64;
    $[190] = t73;
    $[191] = t78;
    $[192] = t84;
    $[193] = t85;
  } else {
    t85 = $[193];
  }
  let t86;
  if ($[194] !== t35 || $[195] !== t85) {
    t86 = <ConfigProvider locale={t35} data-sentry-element="ConfigProvider" data-sentry-source-file="page.tsx">{t85}</ConfigProvider>;
    $[194] = t35;
    $[195] = t85;
    $[196] = t86;
  } else {
    t86 = $[196];
  }
  let t87;
  if ($[197] !== handleSubmit || $[198] !== requestOpen || $[199] !== t31 || $[200] !== t33 || $[201] !== t34 || $[202] !== t86) {
    t87 = <Modal className={styles.request_modal} title={t31} open={requestOpen} onCancel={t32} onOk={handleSubmit} okText={t33} cancelText={t34} destroyOnHidden={true} data-sentry-element="Modal" data-sentry-source-file="page.tsx">{t86}</Modal>;
    $[197] = handleSubmit;
    $[198] = requestOpen;
    $[199] = t31;
    $[200] = t33;
    $[201] = t34;
    $[202] = t86;
    $[203] = t87;
  } else {
    t87 = $[203];
  }
  let t88;
  if ($[204] !== t30 || $[205] !== t87) {
    t88 = <div className={styles.container} data-sentry-component="CalendarPage" data-sentry-source-file="page.tsx">{t30}{t87}</div>;
    $[204] = t30;
    $[205] = t87;
    $[206] = t88;
  } else {
    t88 = $[206];
  }
  return t88;
}
function _temp() {
  window.open(BOOK_URL, "_blank", "noopener,noreferrer");
}